import { alpha, Components, Theme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    small: false;
    medium: false;
    m: true;
  }
}

const getTextFieldTheme = (theme: Theme): ThemeOptions => {
  const textFieldSize = {
    height: theme.spacing(3), // 24px
    padding: `${theme.spacing(2)} ${theme.spacing(1.75)}`, // 16px 14px
  };

  const textFieldTheme: Components<Theme>['MuiTextField'] = {
    defaultProps: {
      variant: 'outlined',
      size: 'm',
    },

    styleOverrides: {
      root: ({ ownerState }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: theme.spacing(1), // 8px

          '& fieldset': {
            border: `1px solid ${theme.palette.M3.sys.light.outline}`,
          },

          '&:hover fieldset': {
            borderColor: theme.palette.M3.sys.light['on-surface'],
          },

          '&.Mui-focused': {
            caretColor: theme.palette.M3.sys.light.primary,

            '& fieldset': {
              border: `3px solid ${theme.palette.M3.sys.light.primary}`,
            },
          },

          '&.Mui-error': {
            caretColor: theme.palette.State.error,

            '& fieldset': {
              border: `3px solid ${theme.palette.State.error}`,
            },
          },

          '&.Mui-disabled fieldset': {
            borderColor: alpha(theme.palette.M3.sys.light['on-surface'], 0.12),
          },
        },

        '& .MuiOutlinedInput-input': {
          color: theme.palette.M3.sys.light['on-surface'],
          ...theme.typography.M3.body.large,
          ...textFieldSize,

          '&::placeholder': {
            color: theme.palette.M3.sys.light['on-surface-variant'],
            opacity: 1,
          },

          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },

        '& .MuiOutlinedInput-root.MuiInputBase-adornedStart .MuiOutlinedInput-input':
          {
            paddingLeft: 0,
          },

        '& .MuiInputLabel-root': {
          color: theme.palette.M3.sys.light['on-surface-variant'],
          ...theme.typography.M3.body.large,
          transform: `translate(${theme.spacing(1.75)}, ${theme.spacing(2)}) scale(1)`, // translate(14px, 16px)

          '&.MuiInputLabel-shrink': {
            transform: `translate(${theme.spacing(1.75)}, ${theme.spacing(-1.125)}) scale(0.75)`, // translate(14px, -9px)
          },

          '&.Mui-disabled': {
            color: alpha(theme.palette.M3.sys.light['on-surface'], 0.38),
          },
        },

        '& .MuiFormHelperText-root': {
          '&.Mui-error': {
            color: theme.palette.State.error,
          },
        },
      }),
    },
  };

  return {
    components: {
      MuiTextField: textFieldTheme,
    },
  };
};

export default getTextFieldTheme;
